import HttpClient from './http-client';
import * as storage from './storage';

const BASE_AUTHORIZATION = 'Basic bGVnZW5kLWFwcC11aTp4ODFJUk5TM2hlOEFsdUFIQ09EVlN2MG1aeWoyUHFuTFd0THZpZ2RIOVJBNVY2OWV2SnlqOFVDdGlSVGg=';
const ACCESS_TOKEN = 'access_token';
const REFRESH_TOKEN = 'refresh_token';
const TOKEN_TYPE = 'token_type';
const TOKEN_PROPS = [ACCESS_TOKEN, REFRESH_TOKEN, TOKEN_TYPE];
const METHODS = ['DELETE', 'GET', 'POST', 'PUT'];

class Api extends HttpClient {
    constructor() {
        super();

        this.storage = storage;

        METHODS.forEach((method) => {
            const methodName = method.toLowerCase();
            const methodType = method.toUpperCase();
            this[methodName] = (url, config = {}) => new Promise((resolve, reject) => {
                const fn = (retry = true) => {
                    this.fetch(methodType, url, this.updateHeaders(config, url))
                        .then(
                            (response) => {
                                resolve(response);
                            },
                            (error) => {
                                if (
                                    error.error === 'invalid_token' &&
                                    !url.includes('token') &&
                                    retry
                                ) {
                                    this.refreshToken()
                                        .then(
                                            () => {
                                                fn(false);
                                            },
                                            () => {
                                                window.location = '/logout';
                                            },
                                        );
                                } else {
                                    reject(error);
                                }
                            },
                        );
                };
                fn();
            });
        });

        this.getAuthenticationSource = this.getAuthenticationSource.bind(this);
        this.getExternalToken = this.getExternalToken.bind(this);
        this.getInternalToken = this.getInternalToken.bind(this);
        this.getToken = this.getToken.bind(this);
        this.refreshExternalToken = this.refreshExternalToken.bind(this);
        this.refreshInternalToken = this.refreshInternalToken.bind(this);
        this.removeToken = this.removeToken.bind(this);
        this.storeToken = this.storeToken.bind(this);
    }

    getToken({
        url,
        headers,
        params,
        source,
    }) {
        const data = Object.keys(params)
            .filter(key => params[key] !== undefined)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
            .join('&');

        return new Promise((resolve, reject) => {
            this.post(url, {
                headers,
                data,
            }).then(
                (response) => {
                    this.storeToken(response.data, source);
                    resolve(response.data);
                },
                (error) => {
                    reject(error);
                },
            );
        });
    }

    getExternalToken(code) {
        return this.getToken({
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            params: {
                grant_type: 'authorization_code',
                redirect_uri: window.location.origin,
                code,
            },
            url: '/public/cognito/o365/oauth2/token',
            source: 'EXTERNAL',
        });
    }

    getInternalToken(username, password) {
        return this.getToken({
            headers: {
                authorization: BASE_AUTHORIZATION,
                'content-type': 'application/x-www-form-urlencoded',
            },
            params: {
                grant_type: 'password',
                username,
                password,
            },
            url: '/oauth/token',
            source: 'INTERNAL',
        });
    }

    refreshInternalToken(refreshToken) {
        return this.getToken({
            headers: {
                authorization: BASE_AUTHORIZATION,
                'content-type': 'application/x-www-form-urlencoded',
            },
            params: {
                grant_type: 'refresh_token',
                refresh_token: refreshToken,
            },
            url: '/oauth/token',
            source: 'INTERNAL',
        });
    }

    refreshExternalToken(refreshToken) {
        return this.getToken({
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            params: {
                grant_type: 'refresh_token',
                refresh_token: refreshToken,
            },
            url: '/public/cognito/o365/oauth2/token',
            source: 'EXTERNAL',
        });
    }

    refreshToken() {
        const refreshToken = this.storage.getItem(REFRESH_TOKEN);
        const authSource = this.getAuthenticationSource();

        if (refreshToken) {
            if (authSource === 'EXTERNAL') {
                return this.refreshExternalToken(refreshToken);
            }

            return this.refreshInternalToken(refreshToken);
        }

        return Promise.reject(new Error('Refresh token not found'));
    }

    getAuthenticationSource() {
        return this.storage.getItem('authorization_source');
    }

    isAuthenticated() {
        const accessToken = this.storage.getItem(ACCESS_TOKEN);

        return !!accessToken;
    }

    getAccessHeader() {
        const accessToken = this.storage.getItem(ACCESS_TOKEN);
        const tokenType = this.storage.getItem(TOKEN_TYPE);

        if (accessToken && tokenType) {
            return `${tokenType} ${accessToken}`;
        }

        return '';
    }

    updateHeaders(config = {}, url) {
        const updatedConfig = Object.assign({}, config);
        if (
            !(updatedConfig.headers && updatedConfig.headers.authorization) &&
            !url.includes('token') &&
            !url.startsWith('/public/')
        ) {
            if (!updatedConfig.headers) {
                updatedConfig.headers = {};
            }

            updatedConfig.headers.authorization = this.getAccessHeader();
        }
        return updatedConfig;
    }

    removeToken() {
        TOKEN_PROPS.forEach((prop) => {
            this.storage.removeItem(prop);
        });
        this.storage.removeItem('authorization_source');
    }

    storeToken(token, source) {
        if (token.challenge_name === undefined) {
            const authSource = source === 'EXTERNAL' ? 'EXTERNAL' : 'INTERNAL';

            TOKEN_PROPS.forEach((prop) => {
                if (token[prop]) {
                    this.storage.setItem(prop, token[prop]);
                }
            });

            this.storage.setItem('authorization_source', authSource);
        }
    }
}

export default new Api();
